import React, { useMemo, useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  Tooltip, Checkbox, FormControlLabel, Box, IconButton
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MappingSelector } from "./mappingSelector";
import { JsonValue, Mapping } from "./editor";

interface JsonTreeProps {
  data: JsonValue;
  path?: string[];
  mappings: Record<string, Mapping>;
  onMappingChange: (path: string, mapping: Mapping) => void;
  onValueChange?: (path: string, newValue: string) => void;
  removeIfNull: Record<string, boolean>;
  setRemoveIfNull: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  removeIfBlank: Record<string, boolean>;
  setRemoveIfBlank: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

type TableRowItem = {
  path: string;
  key: string;
  value: JsonValue;
  displayPath: string;
};

export function JsonTree({
  data,
  path = [],
  mappings,
  onMappingChange,
  onValueChange,
  removeIfBlank,
  setRemoveIfBlank,
  removeIfNull,
  setRemoveIfNull
}: JsonTreeProps) {
  const [editableValues, setEditableValues] = useState<Record<string, string>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const flattenJson = (data: JsonValue, path: string[] = [], displayPath: string[] = []): TableRowItem[] => {
    if (typeof data !== "object" || data === null) {
      return [{
        path: path.join("."),
        key: path[path.length - 1] || "Root",
        value: data,
        displayPath: displayPath.join(" > "),
      }];
    }

    if (Array.isArray(data)) {
      return data.flatMap((item, index) =>
        flattenJson(item, [...path, String(index)], [...displayPath, `[${index}] {}`])
      );
    }

    const currentDisplayPath = displayPath.join(" > ");
    const primitiveEntries = Object.entries(data).filter(([_, val]) => typeof val !== "object" || val === null);
    const objectEntries = Object.entries(data).filter(([_, val]) => typeof val === "object" && val !== null);

    const primitiveRows = primitiveEntries.map(([key, val]) => ({
      path: [...path, key].join("."),
      key,
      value: val,
      displayPath: currentDisplayPath,
    }));

    const objectRows = objectEntries.flatMap(([key, val]) =>
      flattenJson(val, [...path, key], [...displayPath, `${key} ${Array.isArray(val) ? '[ ]' : '{ }'}`])
    );

    return [...primitiveRows, ...objectRows];
  };

  const rows = useMemo(() => flattenJson(data), [data]);

  const groupedRows: Record<string, TableRowItem[]> = useMemo(() => {
    const groups: Record<string, TableRowItem[]> = {};
    rows.forEach((row) => {
      if (!groups[row.displayPath]) {
        groups[row.displayPath] = [];
      }
      groups[row.displayPath].push(row);
    });
    return groups;
  }, [rows]);

  useEffect(() => {
    const initialGroups: Record<string, boolean> = {};
    Object.keys(groupedRows).forEach(key => {
      if (!(key in expandedGroups)) {
        initialGroups[key] = true;
      }
    });
    if (Object.keys(initialGroups).length > 0) {
      setExpandedGroups(prev => ({ ...initialGroups, ...prev }));
    }
  }, [groupedRows]);

  const isPrimitive = (value: JsonValue): value is string | number | boolean | null =>
    typeof value !== "object" || value === null;

  const getValuePreview = (value: JsonValue): string => {
    if (value === null) return "null";
    if (Array.isArray(value)) return `Array(${value.length})`;
    if (typeof value === "object") return `Object(${Object.keys(value).length})`;
    if (typeof value === "string") return value.length > 20 ? `${value.substring(0, 20)}...` : value;
    return String(value);
  };

  const handleValueChange = (path: string, newValue: string) => {
    setEditableValues(prev => ({ ...prev, [path]: newValue }));
    if (onValueChange) {
      onValueChange(path, newValue);
    }
  };

  const handleCheckboxChange = (
    type: "null" | "blank",
    path: string,
    checked: boolean
  ) => {
    if (type === "null") {
      setRemoveIfNull(prev => ({ ...prev, [path]: checked }));
    } else {
      setRemoveIfBlank(prev => ({ ...prev, [path]: checked }));
    }
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const inferJsonType = (value: JsonValue): string => {
    if (Array.isArray(value)) return "array";
    if (value === null) return "null";
    if (typeof value === "object") return "object";
    if (typeof value === "boolean") return "bool";
    if (typeof value === "number") return Number.isInteger(value) ? "int" : "number";
    if (typeof value === "string") {
      const isoTimestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
      if (isoTimestampRegex.test(value)) return "timestamp";
      return "string";
    }
    return typeof value;
  };

  const extractFlagsFromValue = (value: any) => {
    if (typeof value === 'string' && value.startsWith('@')) {
      const parts = value.split(':');
      const removeIfNull = parts[parts.length - 2] === 'true';
      const removeIfBlank = parts[parts.length - 1] === 'true';
      return { removeIfNull, removeIfBlank };
    }
    return { removeIfNull: false, removeIfBlank: false };
  };

  // Automatically populate flags into state based on value strings
  useEffect(() => {
    const newNullFlags: Record<string, boolean> = {};
    const newBlankFlags: Record<string, boolean> = {};

    rows.forEach(({ path, value }) => {
      const { removeIfNull: defaultNull, removeIfBlank: defaultBlank } = extractFlagsFromValue(value);

      if (removeIfNull[path] === undefined && defaultNull) {
        newNullFlags[path] = defaultNull;
      }
      if (removeIfBlank[path] === undefined && defaultBlank) {
        newBlankFlags[path] = defaultBlank;
      }
    });

    if (Object.keys(newNullFlags).length > 0) {
      setRemoveIfNull(prev => ({ ...prev, ...newNullFlags }));
    }
    if (Object.keys(newBlankFlags).length > 0) {
      setRemoveIfBlank(prev => ({ ...prev, ...newBlankFlags }));
    }
  }, [rows]);

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: "#c5c7c8" }}>
          <TableRow>
            <TableCell width="20%"><strong>Key</strong></TableCell>
            <TableCell width="25%"><strong>Value Preview</strong></TableCell>
            <TableCell width="25%"><strong>Mapping</strong></TableCell>
            <TableCell width="30%"><strong>Remove Options</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(groupedRows).map(([groupKey, items]) => (
            <React.Fragment key={groupKey}>
              <TableRow>
                <TableCell colSpan={4} sx={{ backgroundColor: "#f0f0f0" }}>
                  <Box display="flex" alignItems="center">
                    <IconButton size="small" onClick={() => toggleGroup(groupKey)}>
                      {expandedGroups[groupKey] !== false ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <strong>{groupKey || "Root {}"}</strong>
                  </Box>
                </TableCell>
              </TableRow>
              {expandedGroups[groupKey] !== false && items.map(({ path, key, value }) => {
                const nullChecked = removeIfNull[path] ?? false;
                const blankChecked = removeIfBlank[path] ?? false;

                return (
                  <TableRow key={path}>
                    <TableCell width="20%" sx={{ textAlign: "center" }}>
                      <Tooltip title={key || "Root"} arrow>
                        <span>
                          {(key || "Root").length > 20 ? `${(key || "Root").slice(0, 20)}...` : key || "Root"}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell width="20%">
                      {isPrimitive(value) ? (
                        <TextField
                          variant="outlined"
                          size="small"
                          value={editableValues[path] ?? String(value)}
                          onChange={(e) => handleValueChange(path, e.target.value)}
                          fullWidth
                        />
                      ) : (
                        getValuePreview(value)
                      )}
                    </TableCell>
                    <TableCell width="20%">
                      <MappingSelector
                        path={path}
                        mapping={mappings[path] || { option: "", type: inferJsonType(value) }}
                        onChange={(mapping) => onMappingChange(path, mapping)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="row" gap={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={nullChecked}
                              onChange={(e) => handleCheckboxChange("null", path, e.target.checked)}
                            />
                          }
                          label="Remove if Null"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={blankChecked}
                              onChange={(e) => handleCheckboxChange("blank", path, e.target.checked)}
                            />
                          }
                          label="Remove if Blank"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
